import { useFlag } from '@reflag/react-sdk';
import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { ImportEventTrigger } from '@gitbook/app-analytics';
import {
    Description,
    Dialog,
    DialogBody,
    DialogHeader,
    Heading,
    useDialogClose,
} from '@gitbook/spine-react';

import { useCurrentOrganization } from '../../state';
import { EnterpriseMigrationCTA } from '../Billing';
import { ImportContentDialog as ImportContentDialogV2 } from './ImportContentV2/ImportContentDialog';
import { PickImportSourceModalExisting, PickImportSourceModalNew } from './PickImportSourceModal';
import { type ImportTarget, isOrganizationImportTarget } from './types';
import { useContentImport } from './useContentImport';

interface ImportResolvedTarget {
    space: api.Space;
    changeRequestId?: string | undefined;
}

export interface ImportContentDialogProps {
    trigger: ImportEventTrigger;
    target: ImportTarget;
    /** Callback when import is started on the space, if none is provided the modal will be closed */
    onStartImport?: (target: ImportResolvedTarget) => void;
    /** Callback when import is completed */
    onComplete?: (target: ImportResolvedTarget) => void;
    /** Default source */
    defaultSource?: 'file' | 'website';
}

export function ImportContentDialog(props: ImportContentDialogProps): React.ReactElement {
    const importV2Feature = useFlag('IMPORT_V2');
    if (importV2Feature.isEnabled) {
        return (
            <ImportContentDialogV2
                trigger={props.trigger}
                target={props.target}
                onStart={(target) => {
                    props.onStartImport?.(target);
                    props.onComplete?.(target);
                }}
                defaultSource={props.defaultSource}
            />
        );
    }
    return <ImportContentDialogV1 {...props} />;
}

/**
 * Modal to import content into a target.
 */
function ImportContentDialogV1(props: ImportContentDialogProps): React.ReactElement {
    const { target, onStartImport, onComplete } = props;
    const close = useDialogClose();
    const organization = useCurrentOrganization();

    const { modals, source, sources, onSelectSource } = useContentImport({
        onStartImport: (space) => {
            if (onStartImport) {
                onStartImport({ space });
            }
        },
        onComplete: (space) => {
            close();
            onComplete?.({ space });
        },
        onBack: () => onSelectSource(null),
        target,
    });

    const targetIsAnOrganization = isOrganizationImportTarget(target);

    return (
        <Dialog>
            <DialogHeader>
                <Heading>Import your content</Heading>
                <Description>
                    Choose a file type or platform below to get started importing your data.
                </Description>
            </DialogHeader>
            <DialogBody>
                {source ? (
                    modals
                ) : targetIsAnOrganization ? (
                    <PickImportSourceModalNew
                        sources={sources}
                        pickSource={onSelectSource}
                        onClose={close}
                    />
                ) : (
                    <PickImportSourceModalExisting
                        space={target.space}
                        sources={sources}
                        pickSource={onSelectSource}
                        onClose={close}
                    />
                )}
                <EnterpriseMigrationCTA className="mt-6" organization={organization} />
            </DialogBody>
        </Dialog>
    );
}
